import { ReactNode, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  LayoutDashboard, Globe, Package, ShoppingBag, Users,
  Search, Bell, LogOut, ChevronRight, Menu, X, Boxes,
  ClipboardList, Store, Tags, Link2, Handshake, ShieldCheck,
} from 'lucide-react';
import { CommandPalette } from './CommandPalette';
import { useAdminBadges } from './hooks';
import './admin.css';

type NavItem = { to: string; label: string; short: string; icon: any; badgeKey?: 'scrapes' | 'requests' | 'catalogue' | 'orders' | null };
type NavSection = { label: string; items: NavItem[] };

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Operations',
    items: [
      { to: '/admin',           label: 'Overview',      short: 'Home',     icon: LayoutDashboard },
      { to: '/admin/scrapes',   label: 'Scrape & URLs', short: 'Scrape',   icon: Globe,    badgeKey: 'scrapes' },
      { to: '/admin/products',  label: 'Products',      short: 'Products', icon: Boxes,    badgeKey: 'requests' },
      { to: '/admin/catalogue', label: 'Match Quality', short: 'Match',    icon: Package,  badgeKey: 'catalogue' },
      { to: '/admin/orders',    label: 'Orders',        short: 'Orders',   icon: ShoppingBag, badgeKey: 'orders' },
      { to: '/admin/users',     label: 'Users',         short: 'Users',    icon: Users },
    ],
  },
  {
    label: 'Data Management',
    items: [
      { to: '/admin/data/requests',   label: 'Product Requests',    short: 'Requests',   icon: ClipboardList },
      { to: '/admin/data/products',   label: 'Product Management',  short: 'Catalog',    icon: Package },
      { to: '/admin/data/stores',     label: 'Store Management',    short: 'Stores',     icon: Store },
      { to: '/admin/data/categories', label: 'Category Management', short: 'Categories', icon: Tags },
      { to: '/admin/data/scrapes',    label: 'Scrape Management',   short: 'Scrapers',   icon: Link2 },
    ],
  },
  {
    label: 'Admin Tools',
    items: [
      { to: '/admin/approvals', label: 'Approvals Hub', short: 'Approvals', icon: ShieldCheck },
      { to: '/admin/orders',  label: 'Order Management',     short: 'Mgmt',    icon: ShieldCheck },
      { to: '/admin/sellers', label: 'Seller Applications',  short: 'Sellers', icon: Handshake },
      { to: '/admin/suppliers/applications', label: 'Supplier Applications', short: 'Suppliers', icon: Handshake },
      { to: '/admin/foodpreneurs/approvals', label: 'Foodpreneur Approvals', short: 'Foodpreneurs', icon: Handshake },
    ],
  },
];

const NAV = NAV_SECTIONS.flatMap(s => s.items);

const SECTION_LABEL: Record<string, string> = {
  '/admin': 'Overview',
  '/admin/scrapes': 'Scrape & URLs',
  '/admin/products': 'Products',
  '/admin/catalogue': 'Match Quality',
  '/admin/orders': 'Orders',
  '/admin/users': 'Users',
  '/admin/notifications': 'Notifications',
  '/admin/sellers': 'Seller Applications',
  '/admin/suppliers/applications': 'Supplier Applications',
  '/admin/foodpreneurs/approvals': 'Foodpreneur Approvals',
  '/admin/approvals': 'Approvals Hub',
  '/admin/data/requests': 'Product Requests',
  '/admin/data/products': 'Product Management',
  '/admin/data/stores': 'Store Management',
  '/admin/data/categories': 'Category Management',
  '/admin/data/scrapes': 'Scrape Management',
};

export function AdminLayout({ children }: { children: ReactNode }) {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const badges = useAdminBadges();
  const adminName = profile?.full_name || user?.email?.split('@')[0] || 'Admin';
  const initials = adminName.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(p => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  const sectionLabel =
    SECTION_LABEL[pathname] ||
    Object.entries(SECTION_LABEL).find(([k]) => k !== '/admin' && pathname.startsWith(k))?.[1] ||
    'Overview';

  const SidebarInner = (
    <>
      {/* Brand */}
      <div className="px-5 py-4 flex items-center gap-3 border-b border-gray-100">
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold shrink-0 text-sm"
          style={{ background: '#C0392B' }}
        >
          ST
        </div>
        <div className="leading-tight flex-1 min-w-0">
          <p className="text-gray-900 text-sm font-bold truncate">Sis Thoko</p>
          <p className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Admin Console</p>
        </div>
        <button
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden text-gray-500 hover:text-gray-900 p-1"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav */}
      <div className="px-3 py-4 flex-1 overflow-y-auto">
        {NAV_SECTIONS.map((section, sIdx) => (
          <div key={section.label} className={sIdx > 0 ? 'mt-5' : ''}>
            <p className="admin-eyebrow px-3 mb-2">{section.label}</p>
            <nav className="space-y-0.5">
              {section.items.map(item => {
                const Icon = item.icon;
                const count = item.badgeKey ? (badges as any)[item.badgeKey] || 0 : 0;
                return (
                  <NavLink
                    key={`${section.label}-${item.to}-${item.label}`}
                    to={item.to}
                    end={item.to === '/admin'}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                        isActive
                          ? 'bg-red-50 text-red-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {count > 0 && (
                      <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 text-white bg-red-600 min-w-[18px] text-center">
                        {count}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100 flex items-center gap-3">
        <div
          className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
          style={{ background: '#0F172A' }}
        >
          {initials || 'A'}
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-gray-900 text-xs font-semibold truncate">{adminName}</p>
          <p className="text-[10px] text-gray-500">Super admin</p>
        </div>
        <button
          className="text-gray-400 hover:text-red-700 transition p-1.5 rounded-md hover:bg-gray-100"
          onClick={async () => {
            await supabase.auth.signOut();
            navigate('/login', { replace: true });
          }}
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </>
  );

  return (
    <div className="admin-shell flex min-h-screen">
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[240px] shrink-0 bg-white border-r border-gray-200 flex-col min-h-screen sticky top-0">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="relative w-[280px] max-w-[85vw] bg-white flex flex-col border-r border-gray-200">
            {SidebarInner}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 sticky top-0 z-40 flex items-center justify-between px-3 sm:px-6 bg-white border-b border-gray-200 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden h-9 w-9 -ml-1 rounded-lg flex items-center justify-center text-gray-700 hover:bg-gray-100 transition shrink-0"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 text-xs min-w-0">
              <span className="text-gray-400 hidden sm:inline">Admin</span>
              <ChevronRight className="h-3 w-3 text-gray-300 hidden sm:inline" />
              <span className="text-gray-900 font-semibold truncate">{sectionLabel}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500 hover:text-gray-900 hover:border-gray-300 transition"
            >
              <Search className="h-3.5 w-3.5" />
              Search
              <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-white ml-2">⌘K</kbd>
            </button>
            <button
              onClick={() => setPaletteOpen(true)}
              className="sm:hidden h-9 w-9 rounded-lg flex items-center justify-center text-gray-700 hover:bg-gray-100 transition"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigate('/admin/notifications')}
              className="relative h-9 w-9 rounded-lg flex items-center justify-center text-gray-700 hover:bg-gray-100 transition"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
              <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-red-500" />
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-auto min-w-0 pb-24 lg:pb-6">
          {children}
        </main>

        {/* Mobile bottom nav */}
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <ul className="flex items-stretch justify-around overflow-x-auto">
            {NAV_SECTIONS[0].items.map(item => {
              const Icon = item.icon;
              const count = item.badgeKey ? (badges as any)[item.badgeKey] || 0 : 0;
              const active = item.to === '/admin'
                ? pathname === '/admin'
                : pathname.startsWith(item.to);
              return (
                <li key={item.to} className="flex-1 min-w-[58px]">
                  <NavLink
                    to={item.to}
                    className={`relative flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium ${
                      active ? 'text-red-700' : 'text-gray-500'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.short}</span>
                    {count > 0 && (
                      <span className="absolute top-1 right-[18%] text-[9px] font-bold rounded-full px-1 min-w-[14px] text-center text-white bg-red-600">
                        {count}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
